import classNames from 'classnames';
import { CSSProperties } from 'react';
import * as React from 'react';
import { Crop, getPixelCrop, PixelCrop } from 'react-image-crop';

import { Bem } from '../../service/Bem/Bem';
import { CropDecorator } from '../CropDecorator/CropDecorator';

import './ImageCrop.css';

type ImageCropProps = {
    src: string;
    crop: Crop;
    onComplete: (crop: Crop, pixelCrop: Crop) => void;
    onImageLoaded?: (image: HTMLImageElement, pixelCrop: PixelCrop) => void;
    theme?: 'light' | 'dark';
    locked?: boolean;
    cls?: string;
    imageStyle?: CSSProperties;
    minWidth?: number;
    minHeight?: number;
    crossorigin?: 'anonymous' | 'use-credentials';
    keepSelection?: boolean;
    emptyCropDisabled?: boolean;
};

type ImageCropState = {
    crop: Crop;
    newCropPos?: {
        x: number;
        y: number;
    };
};

export class ImageCrop extends React.Component<ImageCropProps, ImageCropState> {
    block = 'image-crop';
    preview: HTMLCanvasElement | null = null;
    image: HTMLImageElement | null;

    constructor(props: ImageCropProps) {
        super(props);

        this.state = {
            crop: props.crop || {
                x: 0,
                y: 0,
                height: 50,
                width: 50
            }
        };
    }

    componentWillReceiveProps(nextProps: ImageCropProps) {
        const { crop, onComplete } = this.props;

        if (crop !== nextProps.crop && nextProps.crop !== this.state.crop) {
            this.setState({
                crop: nextProps.crop
            });

            // костыль, чтобы получать pixelCrop всегда, а не только когда выставляем crop с aspect
            if (this.image) {
                onComplete(
                    nextProps.crop,
                    getPixelCrop(this.image, nextProps.crop)
                );
            }
        }
    }

    render() {
        const {
            src,
            theme = 'light',
            locked,
            cls,
            imageStyle,
            minHeight,
            minWidth,
            crossorigin,
            keepSelection = true
        } = this.props;

        return (
            <div
                className={classNames(
                    Bem.cls({ block: this.block, mods: { theme } }),
                    cls
                )}
            >
                <CropDecorator
                    src={src}
                    crop={this.state.crop}
                    keepSelection={keepSelection}
                    onChange={this.onChange}
                    onImageLoaded={this.onImageLoaded}
                    onComplete={this.onComplete}
                    locked={locked}
                    imageStyle={imageStyle}
                    minWidth={minWidth}
                    minHeight={minHeight}
                    crossorigin={crossorigin}
                    onDragStart={this.onDragStart}
                />
            </div>
        );
    }

    private isCropRespondQuality = (width: number, height: number) => {
        return Math.abs((width * 9) / 16 - height) <= 0.5;
    };

    private onDragStart = () => {
        const { newCropPos, crop } = this.state;
        const { emptyCropDisabled, minWidth, minHeight } = this.props;

        if (!emptyCropDisabled) {
            return;
        }

        if (newCropPos) {
            const width = minWidth || 0.01;
            const height = minHeight || 0.01;

            this.setState({
                crop: {
                    aspect: crop.aspect,
                    x: newCropPos.x + width > 100 ? 100 - width : newCropPos.x,
                    y:
                        newCropPos.y + height > 100
                            ? 100 - height
                            : newCropPos.y,
                    width,
                    height
                },
                newCropPos: undefined
            });
        }
    };

    private onChange = (crop: Crop, pixelCrop: PixelCrop) => {
        const { emptyCropDisabled } = this.props;

        if (
            emptyCropDisabled &&
            ((crop.x && crop.x < 0) || (crop.y && crop.y < 0))
        ) {
            return;
        }

        if (emptyCropDisabled && (crop.height === 0 || crop.width === 0)) {
            this.setState({
                newCropPos: {
                    x: crop.x,
                    y: crop.y
                }
            });
            return;
        }

        this.setState({ crop });
    };

    private onImageLoaded = (image: HTMLImageElement, pixelCrop: PixelCrop) => {
        const { onImageLoaded } = this.props;

        this.image = image;

        if (onImageLoaded) {
            onImageLoaded(image, pixelCrop);
        }
    };

    private onComplete = (crop: Crop, pixelCrop: PixelCrop) => {
        const { onComplete } = this.props;
        const { aspect } = crop;
        const { width, height } = pixelCrop;

        let newWidth = width,
            newHeight = height;

        // Если крополка вышла за грани допустимой ошибки 0.5, то правим разрешение руками
        if (!this.isCropRespondQuality(newWidth, newHeight)) {
            const imageRatio = newWidth / newHeight;

            if (aspect) {
                if (imageRatio > aspect) {
                    newWidth = Math.round(newHeight * aspect);
                } else {
                    newHeight = Math.round(newWidth / aspect);
                }
            }
        }

        onComplete(crop, {
            ...pixelCrop,
            width: newWidth,
            height: newHeight
        });
    };
}
